import { Duration } from '@js-joda/core';

import type {
  PaidBreakExceptionReason,
  PaidBreakExceptionVariant,
  PaidBreakRequestStatus,
} from '#/shared/types/PaidBreakRequest';
import { formatDurationToHours, getHourWord } from '#/shared/utils/dateTimeHelper';

import { PaidBreakExceptionAcceptedContent } from './PaidBreakExceptionAcceptedContent';
import { PaidBreakExceptionContainer } from './PaidBreakExceptionContainer';
import { PaidBreakExceptionErrorContent } from './PaidBreakExceptionErrorContent';
import { PaidBreakExceptionHeader } from './PaidBreakExceptionHeader';
import { PaidBreakExceptionPendingContent } from './PaidBreakExceptionPendingContent';
import { PaidBreakExceptionRejectedContent } from './PaidBreakExceptionRejectedContent';
import { PaidBreakExceptionSummaryContent } from './PaidBreakExceptionSummaryContent';

type PaidBreakExceptionActionProps = {
  breakDuration: string;
  status: PaidBreakRequestStatus;
  onAccept: () => void;
  onReject: () => void;

  // Variant: 'individual' for single request, 'summary' for total/all requests
  variant?: PaidBreakExceptionVariant;

  // Content sections
  reason?: PaidBreakExceptionReason;
  notes?: string;
  approverNotes?: string;
  summaryText?: string;

  // Override options
  subtitle?: string | null; // Override auto-generated subtitle
  statusMessage?: string | null; // Override auto-generated status message
  defaultExpanded?: boolean; // Override default expanded state (defaults: false for accepted/rejected, true for pending/error)
  showRejectionNote?: boolean; // Override "Add rejection note" action (defaults: true for rejected, false otherwise)
  onRejectionNoteOpen?: () => void; // Handler for "Add rejection note"/"Edit" actions
  onRejectionNoteDelete?: () => void; // Handler for "Delete" action

  ariaDescribedBy?: string;
  ariaInvalid?: boolean | 'true' | 'false';
};

export function PaidBreakExceptionAction({
  breakDuration,
  status,
  onAccept,
  onReject,
  variant = 'individual',
  reason,
  notes,
  approverNotes,
  summaryText,
  subtitle,
  statusMessage,
  defaultExpanded,
  showRejectionNote,
  onRejectionNoteOpen,
  onRejectionNoteDelete,
  ariaDescribedBy,
  ariaInvalid,
}: Readonly<PaidBreakExceptionActionProps>) {
  const hours = formatDurationToHours(Duration.parse(breakDuration));

  const title =
    variant === 'summary'
      ? `${hours} ${getHourWord(Number(hours), true)} total paid break exception requested`
      : `${hours} ${getHourWord(Number(hours), true)} paid break exception requested`;

  // Render content based on variant and status
  const renderContent = () => {
    if (variant === 'summary') {
      return <PaidBreakExceptionSummaryContent summaryText={summaryText} />;
    }

    // Individual variant - render based on status
    switch (status) {
      case 'pending':
        return <PaidBreakExceptionPendingContent hours={hours} reason={reason} notes={notes} subtitle={subtitle} />;
      case 'accepted':
        return (
          <PaidBreakExceptionAcceptedContent
            hours={hours}
            reason={reason}
            notes={notes}
            statusMessage={statusMessage}
            defaultExpanded={defaultExpanded}
          />
        );
      case 'rejected':
        return (
          <PaidBreakExceptionRejectedContent
            reason={reason}
            notes={notes}
            approverNotes={approverNotes}
            statusMessage={statusMessage}
            defaultExpanded={defaultExpanded}
            onRejectionNoteOpen={onRejectionNoteOpen}
            onRejectionNoteDelete={onRejectionNoteDelete}
            showRejectionNote={showRejectionNote}
          />
        );
      case 'error':
        return <PaidBreakExceptionErrorContent hours={hours} reason={reason} notes={notes} subtitle={subtitle} />;
      default:
        return null;
    }
  };

  return (
    <PaidBreakExceptionContainer
      status={status}
      variant={variant}
      ariaDescribedBy={ariaDescribedBy}
      ariaInvalid={ariaInvalid}
    >
      <PaidBreakExceptionHeader
        title={title}
        status={status}
        variant={variant}
        onAccept={onAccept}
        onReject={onReject}
      />
      <div>{renderContent()}</div>
    </PaidBreakExceptionContainer>
  );
}
